import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';

import { select, Store } from '@ngrx/store';
import { ServiceProviderService } from '@services';
import { selectRouteState } from '@state/route';

import {
    addServiceProviderAdmin,
    addServiceProviderAdminFailed,
    addServiceProviderAdminSuccess,
    fetchCurrentServiceProvider,
    fetchCurrentServiceProviderAdmins,
    fetchCurrentServiceProviderAdminsFailed,
    fetchCurrentServiceProviderAdminsSuccess,
    fetchServiceProviderById,
    fetchServiceProviderByIdFailed,
    fetchServiceProviderByIdSuccess,
    fetchServiceProviders,
    fetchServiceProvidersFailed,
    fetchServiceProvidersSuccess,
    updateServiceProvider,
    updateServiceProviderFailed,
    updateServiceProviderImage,
    updateServiceProviderImageFailed,
    updateServiceProviderImageSuccess,
    updateServiceProviderSuccess,
} from './service-provider.actions';

@Injectable()
export class ServiceProviderEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private service: ServiceProviderService
  ) {}

  // Read
  fetchServiceProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchServiceProviders),
      switchMap(() =>
        from(this.service.get()).pipe(
          map((serviceProviders) => fetchServiceProvidersSuccess({ serviceProviders })),
          catchError((error) => of(fetchServiceProvidersFailed({ error })))
        )
      )
    )
  );

  fetchServiceProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchServiceProviderById),
      switchMap(({ id }) =>
        from(this.service.getById(id)).pipe(
          map((serviceProvider) => fetchServiceProviderByIdSuccess({ serviceProvider })),
          catchError((error) => of(fetchServiceProviderByIdFailed({ error })))
        )
      )
    )
  );

  fetchCurrentServiceProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCurrentServiceProvider),
      mergeMap(() => this.store.pipe(select(selectRouteState), take(1))),
      switchMap((route) => {
        const id = route.params['serviceProviderId'];
        return of(fetchServiceProviderById({ id }));
      })
    )
  );

  fetchCurrentServiceProviderAdmins$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCurrentServiceProviderAdmins),
      mergeMap(() => this.store.pipe(select(selectRouteState), take(1))),
      switchMap((route) => {
        const serviceProviderId = route.params['serviceProviderId'] as string;
        return from(this.service.getAdmins(serviceProviderId)).pipe(
          map((admins) =>
            fetchCurrentServiceProviderAdminsSuccess({ serviceProviderId, admins })
          ),
          catchError((error) => of(fetchCurrentServiceProviderAdminsFailed({ error })))
        );
      })
    )
  );

  // Update
  updateServiceProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateServiceProvider),
      switchMap(({ serviceProvider }) =>
        from(this.service.update(serviceProvider)).pipe(
          map((serviceProvider) => updateServiceProviderSuccess({ serviceProvider })),
          catchError((error) => of(updateServiceProviderFailed({ error })))
        )
      )
    )
  );

  updateServiceProviderImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateServiceProviderImage),
      switchMap(({ serviceProvider, file }) =>
        from(this.service.updateImage(file, serviceProvider)).pipe(
          map((serviceProvider) => updateServiceProviderImageSuccess({ serviceProvider })),
          catchError((error) => of(updateServiceProviderImageFailed({ error })))
        )
      )
    )
  );

  addServiceProviderAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addServiceProviderAdmin),
      switchMap(({ serviceProvider, admin }) =>
        from(this.service.addAdmin(serviceProvider, admin)).pipe(
          map((admin) => addServiceProviderAdminSuccess({ serviceProvider, admin })),
          catchError((error) => of(addServiceProviderAdminFailed({ error })))
        )
      )
    )
  );
}


